import { useCallback, useEffect, useMemo, useState } from 'react';

import type {
  IServerSideGetRowsParams,
  RowDoubleClickedEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { useSearchParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  useLazyGetNextScheduleQuery,
  useLazyGetScheduledDrugsQuery,
} from '@/app/services/donors';
import { useGetDonorSchedulesByIdsQuery } from '@/app/services/donorSchedules';
import { useLazySearchDrugsQuery } from '@/app/services/lookups';
import { DonorSchedulingActions, PartnershipsActions } from '@/app/slices';
import { GridStatusBar, WithLocalStorage } from '@/components';
import { SELECTION_LIMIT } from '@/constants';
import { useDefaultGridOptions } from '@/grid';
import { sideBar } from '@/grid/defaults';
import { ColDef, ColGroupDef, RowIdParams } from '@/grid/types';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { DonorSchedule } from '@/rtk-query.gql';
import { queryClient } from '@/services';
import { useLazyDonorSchedulesQuery } from '@/services/donor-scheduling/hooks';
import { partnershipsOptions } from '@/services/partnerships';
import { mergeDonorScheduleObjects } from '@/utils';

import { SiteFilterHandle } from '../../DonorSchedulingPartnershipPage';
import {
  useContextMenuActions,
  ValidationIssue,
} from '../../hooks/useContextMenuActions';
import { useContextMenuItems } from '../../hooks/useContextMenuItems';
import {
  useRestDataSource,
  useRTKQueryDataSource,
} from '../../hooks/useRTKQueryDataSource';
import { ClearableField, DonorScheduleBackupSemen } from '../../types';
import {
  findExactDrugByName,
  isNoStimSchedule,
  resolveFshDrug1,
  resolveFshDrug2,
} from '../../util/fshDefaulting';
import { defaultColDef, useDonorScheduleColumnDefs } from './column-defs';
import { ContextMenuDialogs } from './ContextMenuDialogs';
import { getPhysicallySelectedDonorSchedules } from './selection';

interface IGridProps {
  className?: string;
  soScheduleWarningDialogVisible?: boolean;
  setSoScheduleWarningDialogVisible?: (visible: boolean) => void;
  soScheduleValidationIssues?: ValidationIssue[];
  contextMenuActions: ReturnType<typeof useContextMenuActions>;
  onSoScheduleWarningAccept: () => void;
  gridRef: React.RefObject<AgGridReact<DonorSchedule>>;
  onOpenWorkflowHistory?: () => void;
  siteFilterRef?: React.MutableRefObject<SiteFilterHandle>;
}

const STORAGE_KEY = 'donor-scheduling-grid';
const CACHE_BLOCK_SIZE = 500;
const LOAD_DEBOUNCE_MS = 500;

export const Grid = ({
  className = '',
  soScheduleWarningDialogVisible = false,
  setSoScheduleWarningDialogVisible = () => {},
  soScheduleValidationIssues = [],
  contextMenuActions,
  onSoScheduleWarningAccept,
  gridRef,
  onOpenWorkflowHistory,
  siteFilterRef,
}: IGridProps) => {
  const defaultGridOptions = useDefaultGridOptions();
  const dispatch = useAppDispatch();
  const [params, setParams] = useSearchParams();
  const resetForm = params.get('reset');
  const revision = params.get('revision');
  const id = params.get('id');
  const edit = params.get('edit');
  const semenId = params.get('semenId');

  const { mode } = useAppSelector((state) => state.preferences);
  const { quickFilterText, selectedDonorSchedules: appSelectedDonorSchedules } =
    useAppSelector((state) => state.donorScheduling);

  const selectedDonorScheduleIds = appSelectedDonorSchedules.map(
    (ds: DonorSchedule) => ds.id,
  );
  const { data: selectedDonorSchedulesData } = useGetDonorSchedulesByIdsQuery(
    { ids: selectedDonorScheduleIds },
    { skip: selectedDonorScheduleIds.length === 0 },
  );

  const [clearFieldDialogVisible, setClearFieldDialogVisible] = useState(false);
  const [selectedField, setSelectedField] = useState<ClearableField | null>(
    null,
  );
  const [reportDialogVisible, setReportDialogVisible] = useState(false);
  const [missingFieldsDialogVisible, setMissingFieldsDialogVisible] =
    useState(false);

  const { isRest, restQuery, gqlQuery } = useLazyDonorSchedulesQuery();
  const donorScheduleColumnDefs = useDonorScheduleColumnDefs();
  const [getNextSchedule] = useLazyGetNextScheduleQuery();
  const [getScheduledDrugs] = useLazyGetScheduledDrugsQuery();
  const [searchDrugs] = useLazySearchDrugsQuery();
  const fshDrugs = useAppSelector((state) => state.lookups.fshDrugs);
  const isDsDefaultSoEnabled = useFeatureFlag('DS_Default_SO');
  const isPartnershipUpdateEnabled = useFeatureFlag('Partnership_Update');

  const {
    handleClearField: handleClearFieldAction,
    handlePrintDonorToDoReport,
    pendingWorkflow,
  } = contextMenuActions;

  const handleClearField = useCallback(async () => {
    await handleClearFieldAction(selectedField);
    setSelectedField(null);
    setClearFieldDialogVisible(false);
  }, [handleClearFieldAction, selectedField]);

  const openClearFieldDialog = useCallback(() => {
    setClearFieldDialogVisible(true);
  }, []);

  const openReportDialog = useCallback(() => {
    setReportDialogVisible(true);
  }, []);

  const openMissingFieldsDialog = useCallback(() => {
    setMissingFieldsDialogVisible(true);
  }, []);

  const { getContextMenuItems } = useContextMenuItems({
    openClearFieldDialog,
    openReportDialog,
    openMissingFieldsDialog,
    onOpenWorkflowHistory,
  });

  const sessionId = params.get('session');

  const isRevisionMode =
    (revision === 'true' && id !== undefined) ||
    (revision === 'true' && sessionId !== undefined);

  const isNucleusLinkEnabled = useFeatureFlag('Fert_Nucleus_Hyperlinks');

  const { data: revisionData } = useGetDonorSchedulesByIdsQuery(
    { ids: [id] },
    { skip: !id || (!isRevisionMode && !isNucleusLinkEnabled) },
  );

  const isOrFiltering = useFeatureFlag('DS_OR_filtering');
  const isDsFilterMultiSites = useFeatureFlag('DS_Filter_Multi_Sites');
  const isMultiSiteFilterEnabled = isRest && isDsFilterMultiSites;

  const donorSchedulingSideBar = useMemo(
    () => ({
      ...sideBar,
      toolPanels: sideBar.toolPanels?.map((panel) => {
        if (typeof panel === 'string' || panel.id !== 'customViews')
          return panel;
        return {
          ...panel,
          toolPanelParams: {
            ...panel.toolPanelParams,
            ...(isMultiSiteFilterEnabled && { siteFilterRef }),
          },
        };
      }),
    }),
    [isMultiSiteFilterEnabled, siteFilterRef],
  );
  const isFixGridRefreshEnabled = useFeatureFlag('DS_fix_grid_refresh');
  const siteIdsKey = params.getAll('siteId').join(',');

  const stableFertilizationSiteIds = useMemo(
    () => (siteIdsKey ? siteIdsKey.split(',') : []),
    [siteIdsKey],
  );
  const paramsFertilizationSiteIds = useMemo(
    () => params.getAll('siteId'),
    [params],
  );

  const selectedFertilizationSiteIds = isFixGridRefreshEnabled
    ? stableFertilizationSiteIds
    : paramsFertilizationSiteIds;
  const selectedFertilizationSiteIdsKey =
    selectedFertilizationSiteIds?.join(',');

  const donorSchedulesRestQuery = useCallback(
    (queryParams: Parameters<typeof restQuery>[0]) =>
      restQuery({
        ...queryParams,
        fertilizationSiteIds: selectedFertilizationSiteIds,
      }),
    [restQuery, selectedFertilizationSiteIds],
  );

  const { dataSource: gqlDataSource } = useRTKQueryDataSource({
    query: gqlQuery,
    queryName: 'donorSchedules',
    semenId: semenId ?? undefined,
    quickFilterText,
    skip: isRest || Boolean(isRevisionMode),
  });

  const { dataSource: restDataSource } = useRestDataSource({
    query: donorSchedulesRestQuery,
    semenId: semenId ?? undefined,
    quickFilterText,
    skip: !isRest || Boolean(isRevisionMode),
  });

  const serverDataSource = isRest ? restDataSource : gqlDataSource;

  const revisionDataSource = useMemo(() => {
    if (!isRevisionMode || !revisionData) {
      return undefined;
    }

    return {
      getRows: ({ success }: IServerSideGetRowsParams) => {
        const donorSchedules = revisionData?.donorSchedules?.items;
        if (donorSchedules?.length > 0) {
          success({
            rowData: donorSchedules,
            rowCount: donorSchedules.length,
          });
        } else {
          success({
            rowData: [],
            rowCount: 0,
          });
        }
      },
    };
  }, [isRevisionMode, revisionData]);

  const dataSource = isRevisionMode ? revisionDataSource : serverDataSource;

  useEffect(() => {
    if (isRest && !isRevisionMode) {
      gridRef.current?.api?.refreshServerSide({ purge: true });
    }
  }, [gridRef, isRest, isRevisionMode, selectedFertilizationSiteIdsKey]);

  useEffect(() => {
    if (
      selectedDonorSchedulesData?.donorSchedules?.items?.length &&
      appSelectedDonorSchedules?.length
    ) {
      const updatedSelection = appSelectedDonorSchedules
        .map((sel: DonorSchedule) =>
          selectedDonorSchedulesData.donorSchedules.items.find(
            (item) => item.id === sel.id,
          ),
        )
        .filter(Boolean);
      if (
        updatedSelection.length === appSelectedDonorSchedules.length &&
        !updatedSelection.every(
          (item: DonorSchedule, idx: number) =>
            item === appSelectedDonorSchedules[idx],
        )
      ) {
        dispatch({
          type: DonorSchedulingActions.UPDATE_SELECTED_DONOR_SCHEDULES,
          payload: updatedSelection,
        });
        if (
          isPartnershipUpdateEnabled &&
          edit === 'true' &&
          updatedSelection.length === 1
        ) {
          dispatch({
            type: DonorSchedulingActions.UPDATE_WORKING_DONOR_SCHEDULE,
            payload: updatedSelection[0],
          });
        }
      }
    }
  }, [
    selectedDonorSchedulesData,
    appSelectedDonorSchedules,
    dispatch,
    isPartnershipUpdateEnabled,
    edit,
  ]);

  useEffect(() => {
    if (resetForm && gridRef.current) {
      gridRef.current?.api?.deselectAll();
    }
  }, [gridRef, resetForm]);

  const handleRowDoubleClick = useCallback(
    (event: RowDoubleClickedEvent<DonorSchedule>) => {
      const donorSchedule = event.data;
      if (!donorSchedule) return;

      const newParams = new URLSearchParams(params);
      newParams.set('edit', 'true');
      newParams.set('id', donorSchedule ? donorSchedule.id : '-1');
      setParams(newParams);
    },
    [params, setParams],
  );

  const setSemenValues = (payload: DonorScheduleBackupSemen) => {
    return {
      ...payload,
      sire: payload.sire,
      sireId: payload.sireId,
      semenType: payload.semenType,
      semenLocation: payload.semenLocation,
      semenLocationId: payload.semenLocationId,
      semenPrice: payload.semenPrice,
      semenComment: payload.semenComment,
      cane: payload.cane,
      colCode: payload.colCode,
    };
  };

  const setBackupSemenSireValues = (payload: DonorScheduleBackupSemen) => {
    return {
      ...payload,
      backupSemen: payload.backupSemen,
      backupSire: payload.backupSire,
      backupSemenId: payload.backupSemenId,
      backupSireId: payload.backupSireId,
      backupSemenType: payload.backupSemenType,
      backupSemenLocation: payload.backupSemenLocation,
      backupSemenLocationId: payload.backupSemenLocationId,
    };
  };

  const handleDonorScheduleSelection = useCallback(
    async (selectedDonorSchedules: DonorSchedule[]) => {
      if (selectedDonorSchedules.length > 0) {
        const payload =
          selectedDonorSchedules.length === 1
            ? selectedDonorSchedules[0]
            : (mergeDonorScheduleObjects(
                selectedDonorSchedules,
              ) as DonorSchedule);

        dispatch({
          type: DonorSchedulingActions.UPDATE_SELECTED_DONOR_SCHEDULES,
          payload: selectedDonorSchedules,
        });

        const updatedPayload = setBackupSemenSireValues(
          setSemenValues(payload),
        );

        dispatch({
          type: DonorSchedulingActions.UPDATE_WORKING_DONOR_SCHEDULE,
          payload: updatedPayload,
        });

        if (selectedDonorSchedules.length > 1) {
          dispatch({
            type: DonorSchedulingActions.UPDATE_MERGED_DONOR_SCHEDULE,
            payload: updatedPayload,
          });
          const newParams = new URLSearchParams(params);
          newParams.set('multiple', 'true');
          setParams(newParams);
        } else {
          const newParams = new URLSearchParams(params);
          newParams.set('multiple', 'false');
          setParams(newParams);
        }

        if (selectedDonorSchedules.length === 1) {
          const { id, donorId, donor } = selectedDonorSchedules[0];

          if (!id || !donorId || !donor) {
            return;
          }

          const partnershipData = await queryClient.fetchQuery(
            partnershipsOptions({
              donorId: donorId,
              donorScheduleId: id,
            }),
          );
          const partnership = partnershipData?.[0];

          if (partnership) {
            dispatch({
              type: PartnershipsActions.UPDATE_PARTNERSHIP,
              payload: [{ ...partnership, donor, donorId: donor.id }],
            });
          }

          if (isDsDefaultSoEnabled) {
            // Flag ON: skip getNextSchedule if the donor schedule already has a
            // saved schedule master; otherwise fetch and dispatch as normal.
            const existingScheduleMasterId =
              selectedDonorSchedules[0]?.scheduleMasterId;

            let scheduleMasterForDefaulting:
              | {
                  name?: string | null;
                  twiceConcentration?: boolean | null;
                }
              | null
              | undefined = selectedDonorSchedules[0]?.scheduleMaster;

            if (!existingScheduleMasterId) {
              const nextSchedule = await getNextSchedule(donor.id).unwrap();
              const nextScheduleItems = nextSchedule.aspirations.items[0];
              const nextSchedulePayload =
                nextScheduleItems?.nextScheduleMaster ??
                nextScheduleItems?.scheduleMaster;

              scheduleMasterForDefaulting = nextSchedulePayload;

              if (nextSchedulePayload) {
                dispatch({
                  type: DonorSchedulingActions.UPDATE_SCHEDULE_MASTER,
                  payload: {
                    scheduleMaster: nextSchedulePayload,
                    scheduleMasterId: nextSchedulePayload.id,
                  },
                });
              } else {
                dispatch({
                  type: DonorSchedulingActions.UPDATE_SCHEDULE_MASTER,
                  payload: {
                    scheduleMaster: undefined,
                    scheduleMasterId: undefined,
                  },
                });
              }
            }

            const scheduledDrugs = await getScheduledDrugs(donor.id).unwrap();
            const drug2 =
              scheduledDrugs.superOvulationSchedules.items[0]?.drug2;

            const savedSchedule = selectedDonorSchedules[0];
            const hasScheduleMaster = !!scheduleMasterForDefaulting;
            const isNoStim = isNoStimSchedule(
              scheduleMasterForDefaulting?.name,
            );

            if (!hasScheduleMaster || isNoStim) {
              // No schedule master or NO STIM — clear both drugs (no toasts in grid).
              dispatch({
                type: DonorSchedulingActions.UPDATE_FSH_DRUG_1,
                payload: { fshId: undefined, drug: null },
              });
              dispatch({
                type: DonorSchedulingActions.UPDATE_FSH_DRUG_2,
                payload: { fsh2Id: undefined, drug2: null },
              });
            } else {
              const resolveFshDrugByName = async (name: string) => {
                const fromLookup = findExactDrugByName(fshDrugs, name);
                if (fromLookup) {
                  return fromLookup;
                }
                const results = await searchDrugs({
                  search: name,
                  selectionLimit: SELECTION_LIMIT,
                }).unwrap();
                return findExactDrugByName(results, name);
              };

              const drug1Payload = await resolveFshDrug1({
                savedFshId: savedSchedule?.fshId,
                savedDrug: savedSchedule?.drug,
                twiceConcentration:
                  scheduleMasterForDefaulting?.twiceConcentration,
                resolveDrugByName: resolveFshDrugByName,
              });
              dispatch({
                type: DonorSchedulingActions.UPDATE_FSH_DRUG_1,
                payload: drug1Payload,
              });

              const drug2Payload = resolveFshDrug2({
                savedFsh2Id: savedSchedule?.fsh2Id,
                savedDrug2: savedSchedule?.drug2,
                soDrug2: drug2,
              });
              dispatch({
                type: DonorSchedulingActions.UPDATE_FSH_DRUG_2,
                payload: drug2Payload,
              });
            }
          } else {
            // Flag OFF: exact original behavior — always fetch next schedule and
            // dispatch schedule master, then dispatch SO-based FSH drugs.
            const nextSchedule = await getNextSchedule(donor.id).unwrap();
            const nextScheduleItems = nextSchedule.aspirations.items[0];
            const nextSchedulePayload =
              nextScheduleItems?.nextScheduleMaster ??
              nextScheduleItems?.scheduleMaster;

            if (nextSchedulePayload) {
              dispatch({
                type: DonorSchedulingActions.UPDATE_SCHEDULE_MASTER,
                payload: {
                  scheduleMaster: nextSchedulePayload,
                  scheduleMasterId: nextSchedulePayload.id,
                },
              });
            } else {
              dispatch({
                type: DonorSchedulingActions.UPDATE_SCHEDULE_MASTER,
                payload: {
                  scheduleMaster: undefined,
                  scheduleMasterId: undefined,
                },
              });
            }

            const scheduledDrugs = await getScheduledDrugs(donor.id).unwrap();
            const scheduledDrugsItems =
              scheduledDrugs.superOvulationSchedules.items[0];
            const drug1 = scheduledDrugsItems?.drug;
            const drug2 = scheduledDrugsItems?.drug2;

            if (drug1) {
              dispatch({
                type: DonorSchedulingActions.UPDATE_FSH_DRUG_1,
                payload: {
                  fshId: drug1?.id,
                  drug: drug1,
                },
              });
            } else {
              dispatch({
                type: DonorSchedulingActions.UPDATE_FSH_DRUG_1,
                payload: {
                  fshId: undefined,
                  drug: null,
                },
              });
            }
            if (drug2) {
              dispatch({
                type: DonorSchedulingActions.UPDATE_FSH_DRUG_2,
                payload: {
                  fshId: drug2.id,
                  drug: drug2,
                },
              });
            } else {
              dispatch({
                type: DonorSchedulingActions.UPDATE_FSH_DRUG_2,
                payload: {
                  fshId: undefined,
                  drug: null,
                },
              });
            }
          }
        }
      } else {
        dispatch({
          type: DonorSchedulingActions.RESET_SELECTED_DONOR_SCHEDULING,
        });

        dispatch({
          type: DonorSchedulingActions.RESET_WORKING_DONOR_SCHEDULE,
        });
      }
    },
    [
      dispatch,
      getNextSchedule,
      getScheduledDrugs,
      isDsDefaultSoEnabled,
      searchDrugs,
      fshDrugs,
      params,
      setParams,
    ],
  );

  const onSelectionChanged = useCallback(
    async (event: SelectionChangedEvent<DonorSchedule>) => {
      const selectedDonorSchedules = getPhysicallySelectedDonorSchedules(event);

      await handleDonorScheduleSelection(selectedDonorSchedules);
    },
    [handleDonorScheduleSelection],
  );

  const hasSelection = appSelectedDonorSchedules.length > 0;
  useEffect(() => {
    if (
      (revision || (isNucleusLinkEnabled && !hasSelection)) &&
      id &&
      revisionData?.donorSchedules?.items?.[0] &&
      edit === 'true' &&
      id !== '-1'
    ) {
      const donorSchedule = revisionData.donorSchedules.items[0];
      handleDonorScheduleSelection([donorSchedule]);
    }
  }, [
    revision,
    isNucleusLinkEnabled,
    hasSelection,
    id,
    revisionData,
    edit,
    handleDonorScheduleSelection,
  ]);

  const GRID_OPTIONS = useMemo<AgGridReactProps<DonorSchedule>>(
    () => ({
      getRowId: (params: RowIdParams<DonorSchedule>) => String(params.data.id),
      defaultColDef: {
        ...defaultColDef,
        filter: true,
        filterParams: {
          buttons: ['apply', 'clear'],
          closeOnApply: true,
          ...(isOrFiltering ? {} : { maxNumConditions: 1 }),
        },
      },
      columnDefs: donorScheduleColumnDefs as (
        | ColDef<DonorSchedule>
        | ColGroupDef<DonorSchedule>
      )[],
      rowModelType: 'serverSide',
      rowSelection: 'multiple',
      suppressRowClickSelection: false,
      cacheBlockSize: CACHE_BLOCK_SIZE,
      blockLoadDebounceMillis: LOAD_DEBOUNCE_MS,
      statusBar: {
        statusPanels: [
          {
            statusPanel: GridStatusBar,
            statusPanelParams: {
              entityLabel: 'schedules',
              showTotal: !isRest,
            },
            align: 'left' as const,
          },
        ],
      },
    }),
    [donorScheduleColumnDefs, isOrFiltering, isRest],
  );

  const gridClassName = useMemo(() => {
    const themeClass = `ag-theme-transova${mode === 'dark' ? '-dark' : ''}`;
    return `${themeClass} ${className}`.trim();
  }, [mode, className]);

  return (
    <>
      <WithLocalStorage storageKey={STORAGE_KEY}>
        <AgGridReact
          ref={gridRef}
          className={gridClassName}
          gridOptions={defaultGridOptions}
          {...GRID_OPTIONS}
          columnDefs={donorScheduleColumnDefs}
          serverSideDatasource={dataSource}
          onSelectionChanged={onSelectionChanged}
          onRowDoubleClicked={handleRowDoubleClick}
          sideBar={donorSchedulingSideBar}
          getContextMenuItems={getContextMenuItems}
        />
      </WithLocalStorage>
      <ContextMenuDialogs
        clearFieldDialogVisible={clearFieldDialogVisible}
        setClearFieldDialogVisible={setClearFieldDialogVisible}
        selectedField={selectedField}
        setSelectedField={setSelectedField}
        reportDialogVisible={reportDialogVisible}
        setReportDialogVisible={setReportDialogVisible}
        missingFieldsDialogVisible={missingFieldsDialogVisible}
        setMissingFieldsDialogVisible={setMissingFieldsDialogVisible}
        soScheduleWarningDialogVisible={soScheduleWarningDialogVisible}
        setSoScheduleWarningDialogVisible={setSoScheduleWarningDialogVisible}
        soScheduleValidationIssues={soScheduleValidationIssues}
        handleSoScheduleWarningAccept={onSoScheduleWarningAccept}
        appSelectedDonorSchedules={appSelectedDonorSchedules}
        handleClearField={handleClearField}
        handlePrintDonorToDoReport={handlePrintDonorToDoReport}
        pendingWorkflow={pendingWorkflow}
      />
    </>
  );
};
